// CandidateQualificationInfo_new.jsx migration (Phase E). Keys ordered to match the file's own
// reading order: page heading, the 4 Card headers (Educational Details / Typing / Short Hand / Work
// Experience), the Typing/Short Hand checkbox labels, the Work Experience field labels, the
// Add/Remove Experience icon title+alt text, the "Note:" label, then the 3 footer action buttons
// (Cancel / Previous / Save).
//
// Every entry below is an extraction of text that was already bilingual (English + Marathi, either
// "English (मराठी)" parenthetical style for the headings, or the two-stacked-div .displayInGrid
// style for the field labels/buttons) already present in the file — zero new translation authored
// for those.
//
// The 3 entries marked "AI-authored, needs linguistic QA" (addExperienceTitle, removeExperienceTitle,
// noteLabel) had no existing Marathi anywhere in this file. noteLabel reuses the exact Marathi
// (टीप:) already authored for the same "Note:" string in modules/i18n/pages/forgotPassword.js, for
// cross-page consistency. addExperienceTitle/removeExperienceTitle are AI-authored, matching this
// file's own existing terminology for "experience" (अनुभव, e.g. Work Experience -> कामाचा अनुभव).
export default {
  pageHeading: {
    en: 'Candidate Qualification & Experience Details',
    mr: 'उमेदवाराची पात्रता आणि अनुभव तपशील'
  },
  educationalDetailsHeading: { en: 'Educational Details', mr: 'शैक्षणिक तपशील' },
  typingDetailsHeading: {
    en: 'Other Skill details (Typing)',
    mr: 'इतर कौशल्य तपशील (टायपिंग)'
  },
  shortHandDetailsHeading: { en: 'Short Hand Details', mr: 'लघुलेखन' },
  workExperienceHeading: { en: 'Work Experience', mr: 'कामाचा अनुभव' },
  // Phase C.7.4D-R6.4: per-entry "Experience 1"/"Experience 2"... heading. AI-authored, needs
  // linguistic QA — no existing Marathi anywhere in the file for this.
  experienceLabel: { en: 'Experience', mr: 'अनुभव' },

  marathiTyping30Label: { en: 'Marathi Typing (Speed 30)', mr: 'मराठी टायपिंग (वेग ३०)' },
  englishTyping40Label: { en: 'English Typing (Speed 40)', mr: 'इंग्रजी टायपिंग (वेग 40)' },
  wpm100Label: {
    en: '100 Words/Minutes (Marathi/English)',
    mr: '100 शब्द/मिनिटे (मराठी/इंग्रजी)'
  },
  wpm120Label: {
    en: '120 Words/Minutes (Marathi/English)',
    mr: '120 शब्द/मिनिटे (मराठी/इंग्रजी)'
  },

  organizationNameLabel: { en: 'Organization Name', mr: 'संस्थेचे नाव' },
  designationLabel: { en: 'Designation', mr: 'पदनाम' },
  fromDateLabel: { en: 'From Date', mr: 'या तारखेपासून' },
  toDateLabel: { en: 'To Date', mr: 'या तारखेपर्यंत' },

  // AI-authored, needs linguistic QA (no existing Marathi anywhere in the file for these two).
  addExperienceTitle: { en: 'Add Experience', mr: 'अनुभव जोडा' },
  removeExperienceTitle: { en: 'Remove Experience', mr: 'अनुभव काढा' },

  // AI-authored, needs linguistic QA — Marathi matches modules/i18n/pages/forgotPassword.js's
  // noteLabel entry for the same "Note:" string, for cross-page consistency.
  noteLabel: { en: 'Note:', mr: 'टीप:' },

  cancelButton: { en: 'Cancel', mr: 'रद्द करा' },
  previousButton: { en: 'Previous', mr: 'मागे' },
  saveButton: { en: 'Save', mr: 'जतन करा' },

  // Phase C.5 (Candidate System UI Language Migration): QualificationTableHeader_new.jsx (the
  // qualification table's column-header row) and QualificationRow_new.jsx (each editable
  // qualification row) — both confirmed live children of this page, discovered during that phase's
  // repository re-inspection as not covered by the migration above. educationLabel/specializationLabel/
  // boardUniversityInstituteLabel/passingYearLabel/classLabel were already bilingual (extractions,
  // zero new translation authored); gradePercentLabel normalizes the header's "Grade\n/Percent" and the
  // row's "Grade /Percent" spacing variants to one shared "Grade/Percent" wording (both already-existing
  // strings, no new translation authored). selectPassingYearPlaceholder/selectAnOptionPlaceholder had no
  // existing Marathi anywhere in either file — AI-authored, needs linguistic QA.
  educationLabel: { en: 'Education', mr: 'शिक्षण' },
  specializationLabel: { en: 'Specialization', mr: 'विशेषज्ञता' },
  boardUniversityInstituteLabel: { en: 'Board/University/Institute Name', mr: 'बोर्ड/विद्यापीठ/संस्थेचे नाव' },
  passingYearLabel: { en: 'Passing Year', mr: 'उत्तीर्ण वर्ष' },
  gradePercentLabel: { en: 'Grade/Percent', mr: 'ग्रेड/टक्के' },
  classLabel: { en: 'Class', mr: 'वर्ग' },
  // AI-authored, needs linguistic QA
  selectPassingYearPlaceholder: { en: 'Select Passing Year', mr: 'उत्तीर्ण वर्ष निवडा' },
  // AI-authored, needs linguistic QA
  selectAnOptionPlaceholder: { en: 'Select an option', mr: 'एक पर्याय निवडा' },

  // Phase C.7.4D-R6.3 (§4/§5 UI modernization): the per-row post-configured-requirement hint (reads
  // the same PostConfiguration "Qualification Percentage"/"Qualification Grade" rows the client-side
  // validation already consumes — no new data, purely presentational) and the Grade/Percent column's
  // clarifying tooltip. AI-authored, needs linguistic QA (no existing Marathi anywhere in this file
  // for these three).
  minimumPercentageLabel: { en: 'Minimum percentage:', mr: 'किमान टक्केवारी:' },
  acceptedGradesLabel: { en: 'Accepted grades', mr: 'स्वीकार्य श्रेणी' },
  gradePercentHelpText: {
    en: 'Enter a percentage (e.g. 75) or a grade (e.g. A+) — whichever your marksheet shows.',
    mr: 'तुमच्या गुणपत्रिकेनुसार टक्केवारी (उदा. 75) किंवा श्रेणी (उदा. A+) प्रविष्ट करा.'
  },

  // Phase C.7.4D-R6.4 (meaningful UI/UX modernization): a short, ALWAYS-visible caption under the
  // Grade/Percent field (the phase brief explicitly says a tooltip alone is not sufficient), the
  // "Requirement:" prefix on the existing R6.3 hint line, and the visible add/remove row-header text
  // that replaces the old hover-only icon. All AI-authored, needs linguistic QA — no prior Marathi
  // existed anywhere in this file for these four.
  gradePercentShortHint: { en: 'e.g. 75 or A+', mr: 'उदा. 75 किंवा A+' },
  requirementLabel: { en: 'Requirement', mr: 'आवश्यकता' },
  addQualificationPrefix: { en: 'Add', mr: 'जोडा' },
  removeLabel: { en: 'Remove', mr: 'काढा' }
};
